import type { FacilityKind, GoodType, NationId, ProvinceType, Vec2 } from "../types";
import { brokerHubAt } from "../world/brokerOrbit";

/**
 * The fixed continent: 28 provinces on a 7x4 topology, seven states.
 *
 * Geometry begins with a hand-tuned 8x5 point lattice, then gives every shared
 * edge two deterministic bends. Neighbouring polygons therefore reuse the same
 * four-point border path exactly: no seams, but no rectangular tile map either.
 * The adjacency graph still matches what the player sees.
 *
 * The nations are deliberately UNEQUAL, because that imbalance is the engine of
 * the diplomacy: QIN opens with seven provinces, HAN with two and almost no ore,
 * and every king must decide whether that gap warrants war. The map seeds no
 * war or assault; politics, including the first declaration, begins at runtime.
 *
 * Every display name on this board — 지노, 하이젠, 하르베, 아멜른 — is invented for
 * it. The layout came from the Warring States and the ids still carry those
 * readings (`qin-hangu`), but a key is not a name: they are never shown, and
 * every test, save file and stored agent memory is written on them, which is
 * why the cities could be renamed without touching one of them.
 *
 * The whole product is Korean-facing, so every display name is Hangul and every
 * particle is settled by `withParticle` rather than written into the sentence.
 *
 *        c0        c1        c2        c3        c4        c5        c6
 * r0     QIN cap   QIN fac   ZHAO fac  ZHAO cap  ZHAO dep  ZHAO      YAN cap
 * r1     QIN dep   QIN       WEI       WEI cap   WEI dep   YAN       YAN dep
 * r2     QIN       QIN fac   HAN dep   HAN cap   QI        QI cap    QI dep
 * r3     QIN       CHU       CHU cap   CHU       CHU dep   CHU fac   QI
 *
 * Their economies differ in KIND, not only in size, and that is what creates
 * trade: YAN, QI and CHU dig far more ore than their factories can eat, while
 * QIN, ZHAO and above all HAN run lines they cannot feed.
 */

export const MAP_WIDTH = 1580;
export const MAP_HEIGHT = 960;

/** Opening position of the broker fleet, on the sea lane north of the continent. */
export const BROKER_HUB: Vec2 = brokerHubAt({ width: MAP_WIDTH, height: MAP_HEIGHT }, 0);

const GRID_COLS = 7;
const GRID_ROWS = 4;

/** LATTICE[col][row] — 8x5 anchors. Every boundary reuses the same end points. */
const LATTICE: Vec2[][] = [
  [
    { x: 128, y: 252 },
    { x: 56, y: 376 },
    { x: 98, y: 548 },
    { x: 42, y: 742 },
    { x: 138, y: 868 },
  ],
  [
    { x: 280, y: 174 },
    { x: 270, y: 368 },
    { x: 302, y: 566 },
    { x: 276, y: 744 },
    { x: 320, y: 932 },
  ],
  [
    { x: 474, y: 226 },
    { x: 508, y: 398 },
    { x: 476, y: 546 },
    { x: 510, y: 706 },
    { x: 470, y: 872 },
  ],
  [
    { x: 704, y: 152 },
    { x: 688, y: 376 },
    { x: 716, y: 574 },
    { x: 692, y: 732 },
    { x: 740, y: 928 },
  ],
  [
    { x: 916, y: 206 },
    { x: 916, y: 394 },
    { x: 884, y: 540 },
    { x: 912, y: 714 },
    { x: 870, y: 858 },
  ],
  [
    { x: 1080, y: 168 },
    { x: 1096, y: 372 },
    { x: 1124, y: 568 },
    { x: 1100, y: 738 },
    { x: 1160, y: 930 },
  ],
  [
    { x: 1340, y: 236 },
    { x: 1332, y: 400 },
    { x: 1300, y: 550 },
    { x: 1334, y: 710 },
    { x: 1280, y: 862 },
  ],
  [
    { x: 1470, y: 214 },
    { x: 1528, y: 350 },
    { x: 1490, y: 568 },
    { x: 1538, y: 690 },
    { x: 1454, y: 874 },
  ],
];

export interface ProvinceDefinition {
  id: string;
  name: string;
  ownerNationId: NationId;
  type: ProvinceType;
  col: number;
  row: number;
  fortification: number;
  weapons: number;
  /** Baseline ore; createWorld applies STARTING_RESOURCE_MULTIPLIER once. */
  resources: number;
  /** Baseline mines, before the shared opening facility bonus. */
  resourceFacilities: number;
  /** Baseline houses: capital 1, elsewhere 0 if omitted; opening bonus is added later. */
  houses?: number;
  /** Baseline factories, before the shared opening facility bonus. */
  weaponFactories: number;
}

export interface NationDefinition {
  id: NationId;
  name: string;
  color: number;
  accentColor: number;
  personality: string;
  treasury: number;
  buildPriority: Record<FacilityKind, number>;
  morale: number;
  capitalReserveRatio: number;
}

/**
 * Turn order for everything deterministic in the simulation: west to east, then
 * south. `world.nationOrder` is built from this list, so it is also the order
 * alliances, trust and the determinism hash are walked in.
 */
export const NATION_DEFINITIONS: NationDefinition[] = [
  {
    id: "qin",
    name: "지노",
    color: 0xd4453f,
    accentColor: 0xff8a86,
    personality:
      "서쪽에서 일어난 군사 패권국. 법으로 나라를 갈아 동쪽으로 밀어붙인다. 공장은 넘치고 광석은 늘 모자란다.",
    // Richest army, second-richest purse. It can afford to open a war, but the
    // king still has to choose one rather than inheriting it from the map.
    treasury: 1_400_000,
    buildPriority: { resource: 0.62, weapon: 0.28, housing: 0.1 },
    morale: 1.12,
    capitalReserveRatio: 0.15,
  },
  {
    id: "zhao",
    name: "조안",
    color: 0x3b82f6,
    accentColor: 0x93c5fd,
    personality: "북방의 강병. 기병은 사납고 국고는 얇다. 무기를 만들 광석이 늘 부족하다.",
    treasury: 700_000,
    buildPriority: { resource: 0.6, weapon: 0.25, housing: 0.15 },
    morale: 1.06,
    capitalReserveRatio: 0.2,
  },
  {
    id: "wei",
    name: "위본",
    color: 0xa855f7,
    accentColor: 0xd8b4fe,
    personality:
      "대륙 한복판에 노출된 나라. 사방이 국경이라 언제나 누군가와 싸운다. 동맹 없이는 하루도 못 버틴다.",
    // Enough to pay cash for one convoy and not two. After that it borrows.
    treasury: 480_000,
    buildPriority: { resource: 0.38, weapon: 0.47, housing: 0.15 },
    morale: 0.9,
    capitalReserveRatio: 0.3,
  },
  {
    id: "han",
    name: "하이젠",
    color: 0xec4899,
    accentColor: 0xf9a8d4,
    personality: "가장 작고 가장 끼인 나라. 쇠는 잘 다루나 지킬 땅이 없다. 살아남기 위해 무엇이든 판다.",
    // After the opening bonus, ten factories still outrun five mines: HAN
    // remains the nation the broker's credit line exists for.
    treasury: 260_000,
    buildPriority: { resource: 0.58, weapon: 0.17, housing: 0.25 },
    morale: 0.86,
    capitalReserveRatio: 0.35,
  },
  {
    id: "chu",
    name: "캔들",
    color: 0x22c55e,
    accentColor: 0x86efac,
    personality:
      "남방의 거대한 나라. 땅과 광석은 넘치나 조직이 성기다. 값이 맞으면 누구와도 손잡고, 값이 맞으면 누구든 버린다.",
    treasury: 1_000_000,
    buildPriority: { resource: 0.31, weapon: 0.59, housing: 0.1 },
    morale: 1.0,
    capitalReserveRatio: 0.25,
  },
  {
    id: "qi",
    name: "더스트",
    color: 0xf5a524,
    accentColor: 0xfcd34d,
    personality: "동방의 부유한 상업국. 전쟁보다 장부를 믿는다. 남는 것은 팔고 모자란 것은 산다.",
    treasury: 1_600_000,
    buildPriority: { resource: 0.45, weapon: 0.3, housing: 0.25 },
    morale: 0.98,
    capitalReserveRatio: 0.3,
  },
  {
    id: "yan",
    name: "카이트",
    color: 0x14b8a6,
    accentColor: 0x5eead4,
    personality: "북쪽 끝의 외진 나라. 멀어서 안전하고 멀어서 가난하다. 천천히 광석을 쌓으며 남의 전쟁을 지켜본다.",
    treasury: 520_000,
    buildPriority: { resource: 0.44, weapon: 0.36, housing: 0.2 },
    morale: 0.97,
    capitalReserveRatio: 0.3,
  },
];

/** Baseline facilities below receive +2 per kind when createWorld starts a run. */
export const PROVINCE_DEFINITIONS: ProvinceDefinition[] = [
  // ---- QIN (west, 7 provinces): nine factories on five mines ----------------
  {
    id: "qin-xianyang",
    name: "아크베인",
    ownerNationId: "qin",
    type: "capital",
    col: 0,
    row: 0,
    fortification: 0.5,
    weapons: 34,
    resources: 40,
    resourceFacilities: 1,
    weaponFactories: 2,
  },
  {
    id: "qin-yong",
    name: "볼튼",
    ownerNationId: "qin",
    type: "factory",
    col: 1,
    row: 0,
    fortification: 0.28,
    weapons: 22,
    resources: 26,
    resourceFacilities: 0,
    weaponFactories: 3,
  },
  {
    id: "qin-longxi",
    name: "레드가르",
    ownerNationId: "qin",
    type: "depot",
    col: 0,
    row: 1,
    fortification: 0.32,
    weapons: 26,
    resources: 58,
    resourceFacilities: 1,
    weaponFactories: 1,
  },
  {
    id: "qin-hangu",
    name: "간트록",
    ownerNationId: "qin",
    type: "normal",
    col: 1,
    row: 1,
    // The eastern gate, and the fist behind it. Measured: below about 100
    // weapons here the assault on HEXI never builds pressure at all, and the
    // whole opening — QIN winning, everyone else noticing — never starts.
    fortification: 0.15,
    weapons: 104,
    resources: 18,
    resourceFacilities: 0,
    weaponFactories: 0,
  },
  {
    id: "qin-shu",
    name: "세이버",
    ownerNationId: "qin",
    type: "normal",
    col: 0,
    row: 2,
    fortification: 0.2,
    weapons: 20,
    resources: 70,
    resourceFacilities: 2,
    weaponFactories: 0,
  },
  {
    id: "qin-lantian",
    name: "하몬드",
    ownerNationId: "qin",
    type: "factory",
    col: 1,
    row: 2,
    fortification: 0.26,
    weapons: 84,
    resources: 22,
    resourceFacilities: 0,
    weaponFactories: 3,
  },
  {
    id: "qin-ba",
    name: "드레인",
    ownerNationId: "qin",
    type: "normal",
    col: 0,
    row: 3,
    fortification: 0.18,
    weapons: 16,
    resources: 44,
    resourceFacilities: 1,
    weaponFactories: 0,
  },

  // ---- ZHAO (north, 4): hard cavalry on a thin purse and thinner ore --------
  {
    id: "zhao-jinyang",
    name: "스카른",
    ownerNationId: "zhao",
    type: "factory",
    col: 2,
    row: 0,
    fortification: 0.28,
    weapons: 30,
    resources: 24,
    resourceFacilities: 0,
    weaponFactories: 3,
  },
  {
    id: "zhao-handan",
    name: "베르그홀",
    ownerNationId: "zhao",
    type: "capital",
    col: 3,
    row: 0,
    fortification: 0.48,
    weapons: 38,
    resources: 34,
    resourceFacilities: 1,
    weaponFactories: 2,
  },
  {
    id: "zhao-julu",
    name: "빈터가르",
    ownerNationId: "zhao",
    type: "depot",
    col: 4,
    row: 0,
    fortification: 0.3,
    weapons: 26,
    resources: 40,
    resourceFacilities: 1,
    weaponFactories: 1,
  },
  {
    id: "zhao-dai",
    name: "노르텐",
    ownerNationId: "zhao",
    type: "normal",
    col: 5,
    row: 0,
    fortification: 0.2,
    weapons: 34,
    resources: 20,
    resourceFacilities: 1,
    weaponFactories: 0,
  },

  // ---- WEI (centre, 3): five borders and nothing to spare -------------------
  {
    id: "wei-hexi",
    name: "하르베",
    ownerNationId: "wei",
    type: "normal",
    col: 2,
    row: 1,
    // A heavily armed western gate. If a king opens this front, it can sustain
    // an immediate offensive without the map itself prescribing that war.
    fortification: 0.3,
    weapons: 66,
    resources: 22,
    resourceFacilities: 1,
    weaponFactories: 0,
  },
  {
    id: "wei-daliang",
    name: "메르덴",
    ownerNationId: "wei",
    type: "capital",
    col: 3,
    row: 1,
    fortification: 0.46,
    weapons: 28,
    resources: 36,
    resourceFacilities: 1,
    weaponFactories: 2,
  },
  {
    id: "wei-anyi",
    name: "소른펠",
    ownerNationId: "wei",
    type: "depot",
    col: 4,
    row: 1,
    fortification: 0.3,
    weapons: 20,
    resources: 44,
    resourceFacilities: 1,
    weaponFactories: 2,
  },

  // ---- HAN (squeezed, 2): the best ironworks on the board and no ore --------
  {
    id: "han-yiyang",
    name: "슈타흐",
    ownerNationId: "han",
    type: "depot",
    col: 2,
    row: 2,
    fortification: 0.32,
    weapons: 40,
    resources: 30,
    resourceFacilities: 0,
    weaponFactories: 4,
  },
  {
    id: "han-xinzheng",
    name: "키르센",
    ownerNationId: "han",
    type: "capital",
    col: 3,
    row: 2,
    fortification: 0.44,
    weapons: 22,
    resources: 26,
    resourceFacilities: 1,
    weaponFactories: 2,
  },

  // ---- QI (east, 4): the wealthy surplus the broker drains ------------------
  {
    id: "qi-pingyuan",
    name: "살렌",
    ownerNationId: "qi",
    type: "normal",
    col: 4,
    row: 2,
    fortification: 0.24,
    weapons: 24,
    resources: 56,
    resourceFacilities: 2,
    weaponFactories: 0,
  },
  {
    id: "qi-linzi",
    name: "포르티스",
    ownerNationId: "qi",
    type: "capital",
    col: 5,
    row: 2,
    fortification: 0.46,
    weapons: 30,
    resources: 70,
    resourceFacilities: 2,
    weaponFactories: 2,
  },
  {
    id: "qi-jimo",
    name: "아멜른",
    ownerNationId: "qi",
    type: "depot",
    col: 6,
    row: 2,
    fortification: 0.32,
    weapons: 78,
    resources: 190,
    resourceFacilities: 3,
    weaponFactories: 1,
  },
  {
    id: "qi-langya",
    name: "케이든",
    ownerNationId: "qi",
    type: "normal",
    col: 6,
    row: 3,
    fortification: 0.2,
    weapons: 18,
    resources: 60,
    resourceFacilities: 2,
    weaponFactories: 0,
  },

  // ---- CHU (south, 5): all the land, none of the industry -------------------
  {
    id: "chu-qianzhong",
    name: "모르가스",
    ownerNationId: "chu",
    type: "normal",
    col: 1,
    row: 3,
    fortification: 0.2,
    weapons: 26,
    resources: 62,
    resourceFacilities: 2,
    weaponFactories: 0,
  },
  {
    id: "chu-ying",
    name: "바스티온",
    ownerNationId: "chu",
    type: "capital",
    col: 2,
    row: 3,
    fortification: 0.44,
    weapons: 32,
    resources: 74,
    resourceFacilities: 2,
    weaponFactories: 1,
  },
  {
    id: "chu-yunmeng",
    name: "미르하",
    ownerNationId: "chu",
    type: "normal",
    col: 3,
    row: 3,
    fortification: 0.18,
    weapons: 22,
    resources: 58,
    resourceFacilities: 2,
    weaponFactories: 0,
  },
  {
    id: "chu-chencheng",
    name: "글렌토르",
    ownerNationId: "chu",
    type: "depot",
    col: 4,
    row: 3,
    fortification: 0.3,
    weapons: 44,
    resources: 120,
    resourceFacilities: 2,
    weaponFactories: 1,
  },
  {
    id: "chu-huaiyang",
    name: "로우엔",
    ownerNationId: "chu",
    type: "factory",
    col: 5,
    row: 3,
    fortification: 0.26,
    weapons: 20,
    resources: 48,
    resourceFacilities: 1,
    weaponFactories: 2,
  },

  // ---- YAN (far north, 3): ore, distance and no factories to speak of -------
  {
    id: "yan-ji",
    name: "아스틸",
    ownerNationId: "yan",
    type: "capital",
    col: 6,
    row: 0,
    fortification: 0.42,
    weapons: 18,
    resources: 66,
    resourceFacilities: 3,
    weaponFactories: 0,
  },
  {
    id: "yan-shanggu",
    name: "크로반",
    ownerNationId: "yan",
    type: "normal",
    col: 5,
    row: 1,
    fortification: 0.22,
    weapons: 16,
    resources: 48,
    resourceFacilities: 2,
    weaponFactories: 0,
  },
  {
    id: "yan-liaodong",
    name: "툴레마",
    ownerNationId: "yan",
    type: "depot",
    col: 6,
    row: 1,
    fortification: 0.3,
    weapons: 14,
    resources: 130,
    resourceFacilities: 3,
    weaponFactories: 1,
  },
];

/** Iteration order for every per-nation loop outside the simulation. */
export const NATION_IDS: readonly NationId[] = NATION_DEFINITIONS.map((n) => n.id);

function definitionAt(col: number, row: number): ProvinceDefinition | undefined {
  return PROVINCE_DEFINITIONS.find((p) => p.col === col && p.row === row);
}

interface EdgeShape {
  firstT: number;
  secondT: number;
  firstNormal: number;
  secondNormal: number;
}

/**
 * Repeating directions, not randomness. The strongest bend is 28 units, under
 * 20% of the narrowest 146-unit row: visible at opening zoom while leaving at
 * least 90 units between neighbouring horizontal paths even in the worst case.
 */
const EDGE_SHAPES: readonly EdgeShape[] = [
  { firstT: 0.26, secondT: 0.64, firstNormal: 22, secondNormal: -18 },
  { firstT: 0.34, secondT: 0.71, firstNormal: -26, secondNormal: 14 },
  { firstT: 0.29, secondT: 0.68, firstNormal: 18, secondNormal: 27 },
  { firstT: 0.31, secondT: 0.74, firstNormal: -15, secondNormal: -28 },
  { firstT: 0.24, secondT: 0.59, firstNormal: 28, secondNormal: -12 },
  { firstT: 0.38, secondT: 0.69, firstNormal: -20, secondNormal: 25 },
  { firstT: 0.27, secondT: 0.72, firstNormal: 13, secondNormal: -27 },
  { firstT: 0.33, secondT: 0.62, firstNormal: -28, secondNormal: 18 },
] as const;

function bentEdge(a: Vec2, b: Vec2, shapeIndex: number, normalScale = 1): Vec2[] {
  const shape = EDGE_SHAPES[shapeIndex % EDGE_SHAPES.length];
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const length = Math.max(1, Math.hypot(dx, dy));
  const normalX = -dy / length;
  const normalY = dx / length;
  const pointAt = (t: number, normal: number): Vec2 => ({
    x: a.x + dx * t + normalX * normal,
    y: a.y + dy * t + normalY * normal,
  });
  return [
    { ...a },
    pointAt(shape.firstT, shape.firstNormal * normalScale),
    pointAt(shape.secondT, shape.secondNormal * normalScale),
    { ...b },
  ];
}

function horizontalBoundary(col: number, row: number): Vec2[] {
  return bentEdge(LATTICE[col][row], LATTICE[col + 1][row], row * GRID_COLS + col);
}

function verticalBoundary(col: number, row: number): Vec2[] {
  const horizontalCount = (GRID_ROWS + 1) * GRID_COLS;
  return bentEdge(
    LATTICE[col][row],
    LATTICE[col][row + 1],
    horizontalCount + col * GRID_ROWS + row,
    // Banners stand on side-by-side seams. Half-amplitude keeps those paths
    // irregular without pushing them into the figure columns on either side.
    0.5,
  );
}

/** Twelve-point province polygon, clockwise from the top-left lattice anchor. */
export function provincePolygon(def: ProvinceDefinition): Vec2[] {
  const { col, row } = def;
  const top = horizontalBoundary(col, row);
  const right = verticalBoundary(col + 1, row);
  const bottom = [...horizontalBoundary(col, row + 1)].reverse();
  const left = [...verticalBoundary(col, row)].reverse();
  return [...top, ...right.slice(1), ...bottom.slice(1), ...left.slice(1, -1)];
}

export function polygonCenter(polygon: Vec2[]): Vec2 {
  let x = 0;
  let y = 0;
  for (const p of polygon) {
    x += p.x;
    y += p.y;
  }
  return { x: x / polygon.length, y: y / polygon.length };
}

/** Four-neighbour grid adjacency. */
export function provinceNeighbours(def: ProvinceDefinition): string[] {
  const deltas: Array<[number, number]> = [
    [0, -1],
    [1, 0],
    [0, 1],
    [-1, 0],
  ];
  const out: string[] = [];
  for (const [dc, dr] of deltas) {
    const c = def.col + dc;
    const r = def.row + dr;
    if (c < 0 || c >= GRID_COLS || r < 0 || r >= GRID_ROWS) continue;
    const neighbour = definitionAt(c, r);
    if (neighbour) out.push(neighbour.id);
  }
  return out;
}

/** Deliberate opening book: the spread is readable in the first five seconds. */
export interface SeedOrderDefinition {
  nationId: NationId;
  side: "BUY" | "SELL";
  goodType: GoodType;
  quantity: number;
  unitPrice: number;
  reason: string;
}

/**
 * Both goods, both directions, and the asymmetry visible at a glance: the three
 * ore-rich states are asking a fifth of what the three factory-rich ones bid.
 */
export const SEED_MARKET_ORDERS: SeedOrderDefinition[] = [
  {
    nationId: "qi",
    side: "SELL",
    goodType: "weapons",
    quantity: 40,
    unitPrice: 17_500,
    reason: "동방의 잉여 병장기 정리",
  },
  {
    nationId: "qi",
    side: "SELL",
    goodType: "resources",
    quantity: 90,
    unitPrice: 5_200,
    reason: "아멜른 창고가 넘친다",
  },
  {
    nationId: "yan",
    side: "SELL",
    goodType: "resources",
    quantity: 70,
    unitPrice: 4_800,
    reason: "툴레마 광석 적체",
  },
  {
    nationId: "chu",
    side: "SELL",
    goodType: "resources",
    quantity: 60,
    unitPrice: 5_600,
    reason: "미르하의 광석은 쓸 곳이 없다",
  },
  {
    nationId: "wei",
    side: "BUY",
    goodType: "weapons",
    quantity: 30,
    unitPrice: 42_000,
    reason: "하르베 전선 긴급",
  },
  {
    nationId: "han",
    side: "BUY",
    goodType: "resources",
    quantity: 40,
    unitPrice: 11_000,
    reason: "슈타흐 대장간이 멎는다",
  },
  {
    nationId: "qin",
    side: "BUY",
    goodType: "resources",
    quantity: 60,
    unitPrice: 10_500,
    reason: "간트록 동진에 원료가 모자란다",
  },
  {
    nationId: "zhao",
    side: "BUY",
    goodType: "resources",
    quantity: 40,
    unitPrice: 9_500,
    reason: "베르그홀 공방 가동률 유지",
  },
  {
    nationId: "chu",
    side: "BUY",
    goodType: "weapons",
    quantity: 24,
    unitPrice: 33_000,
    reason: "북쪽 국경 수비 보강",
  },
];
